import { useState, useMemo } from "react";
import { Card } from "../common/Card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../common/Select";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

export function RevenueChart({ reportSummaryData }) {
  const [period, setPeriod] = useState("month");
  const [category, setCategory] = useState("all");
  const [agentType, setAgentType] = useState("all");

  const chartData = useMemo(() => {
    if (!reportSummaryData) {
      return [];
    }

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    
    return months.map((month, index) => {
      const dataPoint = { name: month };
      
      if (Array.isArray(reportSummaryData)) {
        reportSummaryData.forEach(item => {
          if (item.label && Array.isArray(item.data) && item.data[index] !== undefined) {
            dataPoint[item.label] = item.data[index] || 0;
          }
        });
      } 
      else if (reportSummaryData.label && Array.isArray(reportSummaryData.data)) {
        dataPoint[reportSummaryData.label] = reportSummaryData.data[index] || 0;
      }
      
      return dataPoint;
    });
  }, [reportSummaryData]);

  const availableCategories = useMemo(() => {
    if (!chartData.length) return [];
    
    const firstData = chartData[0];
    return Object.keys(firstData).filter(key => key !== 'name');
  }, [chartData]);

  const categoryColors = {
    "Flights": "#1E61DC",
    "Hotels": "#10B981",
    "Tours": "#F59E0B",
    "Packages": "#8B5CF6",
    "Subscriptions": "#EC4899",
    "Post-Ticketing": "#06B6D4",
    "TravX": "#8B5CF6", 
    "Insurance": "#EF4444",
  };

  const defaultColors = [
    "#1E61DC", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899", "#06B6D4", 
    "#EF4444", "#8B5CF6", "#14B8A6", "#F97316"
  ];

  return (
    <Card className="p-6 bg-white rounded-lg shadow-sm border border-gray-100">
      {/* Filters */}
      <div className="flex items-center gap-4 mb-6 flex-wrap">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Period" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Day</SelectItem>
            <SelectItem value="week">Week</SelectItem>
            <SelectItem value="month">Month</SelectItem>
            <SelectItem value="quarter">Quarter</SelectItem>
          </SelectContent>
        </Select>

        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Product Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {availableCategories.map(cat => (
              <SelectItem key={cat} value={cat.toLowerCase()}>
                {cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={agentType} onValueChange={setAgentType}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Agent Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Agents</SelectItem>
            <SelectItem value="corporate">Corporate</SelectItem>
            <SelectItem value="independent">Independent</SelectItem>
            <SelectItem value="partner">Partner</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis dataKey="name" stroke="#6B7280" />
          <YAxis stroke="#6B7280" />
          <Tooltip
            contentStyle={{
              backgroundColor: "white",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
            }}
            formatter={(value) => `₦${value.toLocaleString()}`}
          />
          <Legend />
          
          {/* Dynamically render lines based on available categories */}
          {availableCategories.map((cat, index) => (
            <Line
              key={cat}
              type="monotone"
              dataKey={cat}
              stroke={categoryColors[cat] || defaultColors[index % defaultColors.length]}
              strokeWidth={2}
              dot={{ fill: categoryColors[cat] || defaultColors[index % defaultColors.length] }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
}